import type {PhotoRequirement} from './types';

/** How an element is placed on the page. */
export type PositionMode = NonNullable<PhotoRequirement['position_mode']>;

/**
 * An element's position mode, with the stored-`undefined` default applied.
 *
 * `position_mode` is optional on the wire, and "unset means flow" is the answer every
 * reader needs — the renderers, the toolbar's Flow/Abs toggle, and which pair of
 * fields sizes the element (see lib/element-size). It lived as a hand-written
 * `?? 'flow'` at each of those, which is how the ContextualToolbar's two branches
 * ended up defaulting it differently.
 *
 * Not for group children: a child of an absolute group renders as absolute whatever
 * it stores, and that lookup needs the group sentinel — see `effectivePositionModeIn`.
 */
export function effectivePositionMode(element: PhotoRequirement): PositionMode {
  return element.position_mode ?? 'flow';
}

/**
 * The mode an element actually RENDERS in, given the groups around it.
 *
 * A child of an absolute group is absolute whatever it stores, so the whole
 * group moves as one unit — and that answer needs the sentinel, which is why it
 * cannot live in `effectivePositionMode` above.
 *
 * **One reader, because there were four.** The rule was hand-written in
 * `usePreviewLayout` (what draws where), `useCanvasDrag` (what can be dragged)
 * and `placeElementsOnPage` (where a paste lands) — three surfaces that have to
 * agree about one element or it is drawn in one place, dragged in another and
 * pasted onto a third. It belongs beside the `?? 'flow'` default it extends.
 *
 * `sentinels` maps a group id to its sentinel element. A `group_id` with no
 * sentinel in the map falls back to the element's own mode: for a caller holding
 * the whole document that means an orphaned group id, and for one holding a
 * subset (a clipboard) it means the group's sentinel was not copied along with
 * it — in both cases the element answers for itself, which is what it stores.
 */
export function effectivePositionModeIn(
  element: PhotoRequirement,
  sentinels: ReadonlyMap<string, PhotoRequirement>,
): PositionMode {
  const sentinel = element.group_id ? sentinels.get(element.group_id) : undefined;
  if (sentinel?.position_mode === 'absolute') return 'absolute';
  return effectivePositionMode(element);
}

/** The group sentinels in a list, keyed by id — the lookup
 *  `effectivePositionModeIn` takes. */
export function sentinelsById(
  requirements: readonly PhotoRequirement[],
): ReadonlyMap<string, PhotoRequirement> {
  return new Map(requirements.filter((r) => r.type === 'group').map((r) => [r.id, r]));
}
